import { z } from 'zod';
// From the leaf `./ids.js`, NOT `./conversations.js` — `conversations.ts`
// imports `CommentPreviewDTO` FROM this file, so importing `ConversationId`
// from `conversations.ts` here would recreate the load-order cycle this file
// was split out to avoid (see `ids.ts`'s doc comment and `comments.test.ts`).
import { ConversationId } from './ids.js';
/**
 * Admin-facing Zod schemas for internal (operator-only, never sent to the
 * customer) comments on a conversation. A comment may be attached to a
 * status-change event (`statusEventId`) — the "reason" note an operator
 * leaves when moving a conversation to a terminal status via
 * `UpdateConversationRequest.comment` — or stand alone, added directly from
 * the conversation rail.
 */
export const CommentId = z.string().uuid();
export const CommentDTO = z.object({
    id: CommentId,
    conversationId: ConversationId,
    author: z.object({ id: z.string(), displayName: z.string() }),
    body: z.string(),
    statusEventId: z.string().nullable(),
    createdAt: z.string().datetime(),
    /**
     * Last-edit stamp; `null` = never edited. `createdAt` is NOT moved by an
     * edit (feed order / keyset cursor are keyed on it). `.default(null)` so a
     * new bundle can read a payload from an admin-api that predates the edit
     * endpoint during a rollout window.
     */
    updatedAt: z.string().datetime().nullable().default(null),
    /** Who edited last — may differ from `author` (`comment.manage`); `null` = never edited. */
    editedBy: z.object({ id: z.string(), displayName: z.string() }).nullable().default(null),
});
export const AddCommentRequest = z.object({
    body: z.string().trim().min(1).max(4000),
});
/** `PATCH /api/comments/:id` — the body is the only editable field. */
export const UpdateCommentRequest = z.object({
    body: z.string().trim().min(1).max(4000),
});
/**
 * One row of the cross-conversation comment feed (`GET /api/comments`) — a
 * comment plus enough of its parent conversation to render a feed row
 * without a second round trip per item.
 */
export const CommentFeedItemDTO = CommentDTO.extend({
    conversation: z.object({
        id: ConversationId,
        number: z.number().int().nullable(),
        subject: z.string().nullable(),
        contactDisplayName: z.string().nullable(),
    }),
});
export const CommentFeedQuery = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    authorId: z.string().uuid().optional(),
    q: z.string().min(1).max(200).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
export const CommentFeedResponse = z.object({
    items: z.array(CommentFeedItemDTO),
    nextCursor: z.string().nullable(),
});
/**
 * Lean preview of the most recent comment — carried inline on
 * `ConversationListItemDTO.lastComment` so the inbox row can show a snippet
 * without joining the full `CommentDTO` (no `id`/`conversationId`/`statusEventId`).
 */
export const CommentPreviewDTO = z.object({
    body: z.string(),
    authorDisplayName: z.string(),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=comments.js.map